import type { TFunction } from "i18next"
import React, { createContext, useContext, useState } from "react"

import { useAppContext } from "@contexts/app_context"
import { getCoupons } from "@domain/coupons"
import useToggle from "@hooks/use_toggle"
import { useQuery } from "@tanstack/react-query"
import i18next from "@utils/localization"
import { CouponCode } from "__generated__/graphql"

type CouponsPageContextProps = {
  translate: TFunction<"translation", undefined>
  isLeftDrawerOpen?: boolean
  toggleIsLeftDrawerOpen?: () => void
  couponsData?: CouponCode[]
  setCouponsData?: React.Dispatch<React.SetStateAction<CouponCode[]>>
  search?: string
  setSearch: (search: string) => void
  isLoading: boolean
  pagination?: {
    page: number
    pageSize: number
  }
  setPagination?: React.Dispatch<
    React.SetStateAction<{
      page: number
      pageSize: number
    }>
  >
  isOpenDeleteCoupons?: boolean
  toggleDeleteCoupons?: () => void
}

const defaultValues: CouponsPageContextProps = {
  translate: i18next.t,
  isLoading: true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSearch: (search: string) => {}
}

const CouponsPageContext = createContext(defaultValues)

export function CouponsPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate } = useAppContext()
  const [isLeftDrawerOpen, toggleIsLeftDrawerOpen] = useToggle(true)
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20
  })
  const [search, setSearch] = useState("")
  const [isOpenDeleteCoupons, toggleDeleteCoupons] = useToggle()

  const { data: couponsData, isLoading } = useQuery({
    queryKey: ["coupons-data-table", pagination, search],
    queryFn: async () => {
      const response = await getCoupons({
        limit: pagination.pageSize,
        page: pagination.page + 1,
        search
      })
      return response.data?.data as CouponCode[]
    }
  })

  return (
    <CouponsPageContext.Provider
      value={{
        ...defaultValues,
        translate,
        isLeftDrawerOpen,
        toggleIsLeftDrawerOpen,
        couponsData,
        search,
        setSearch,
        pagination,
        setPagination,
        isLoading,
        isOpenDeleteCoupons,
        toggleDeleteCoupons
      }}
    >
      {props.children}
    </CouponsPageContext.Provider>
  )
}

export function useCouponsPageContext() {
  const context = useContext(CouponsPageContext)
  if (context === undefined) {
    throw new Error("UserPage Context Error")
  }
  return context
}
